import type { NavigateFunction } from 'react-router-dom';
import { reportsApi, type SafetyDashboardRow } from '../../services/reportsApi';
import {
  buildRepeatSafetyScanPath,
  buildCreateDraftPayloadFromHandoff,
  buildHandoffFromDashboardRow,
} from '../safetyCases/safetyScanWorkspaceHandoff';
import {
  buildCurrentScanReviewPath,
} from './patternReviewScanDeepLink';
import {
  buildLiveSafetyCasePath,
  resolveSafetyCaseId,
  resolveSignedSafetyScanReportId,
} from '../../utils/acceptanceHierarchyNavigation';
import { safetyScanVerifyPath } from '../acceptance/scanVerifyNavigation';
import type { RiskPatternWorkflowReviewActions } from '../../components/incidents/RiskPatternWorkflowReviewContextPanel';
import {
  resolveRiskPatternReviewPageState,
  type RiskPatternWorkflowReviewContext,
} from './riskPatternWorkflowReviewContext';

export function resolveCreatedSafetyCaseId(result: {
  finding?: { safety_case_id?: string | null; finding_id?: string | null } | null;
} | null | undefined): string {
  return String(result?.finding?.safety_case_id || result?.finding?.finding_id || '').trim();
}

export function buildRiskPatternWorkflowReviewActions(args: {
  context: RiskPatternWorkflowReviewContext;
  dashboardRow: SafetyDashboardRow | null;
  navigate: NavigateFunction;
  orgId: string;
  siteId: string;
  reportFrom?: string | null;
  reportTo?: string | null;
  scanScopeStart?: string | null;
  scanScopeEnd?: string | null;
  signedScanAt?: string | null;
  signedScanScopeStart?: string | null;
  signedScanScopeEnd?: string | null;
  latestSignedScanId?: string | null;
  newActivityWindowStart?: string | null;
  onReviewEvidence: () => void;
  onMarkFalsePositive: () => void;
  onNeedsMoreReview: () => void;
  onContinueReview?: () => void;
  canConfigureScheduledScans?: boolean;
  onSafetyCaseCreated?: (safetyCaseId: string) => void;
}): RiskPatternWorkflowReviewActions {
  const row = args.dashboardRow;
  const pageState = resolveRiskPatternReviewPageState(args.context);

  const openSafetyCase = () => {
    const caseId = args.context.safetyCaseId || (row ? resolveSafetyCaseId(row) : '');
    if (!caseId) return;
    args.navigate(buildLiveSafetyCasePath({
      caseId,
      orgId: args.orgId,
      siteId: String(row?.site_id || args.siteId).trim() || undefined,
      workflowId: args.context.workflowId,
      incidentId: args.context.representativeIncidentId || undefined,
    }));
  };

  const viewSignedScan = () => {
    if (!row) return;
    const reportId = resolveSignedSafetyScanReportId(row);
    if (!reportId) return;
    args.navigate(safetyScanVerifyPath(reportId, {
      orgId: args.orgId,
      siteId: String(row.site_id || args.siteId).trim() || null,
      safetyCaseId: resolveSafetyCaseId(row) || args.context.safetyCaseId || null,
      incidentId: args.context.representativeIncidentId || row.representative_incident_id || null,
    }));
  };

  const currentScanReviewPath = buildCurrentScanReviewPath({
    dashboardRow: row,
    safetyCaseId: args.context.safetyCaseId,
    orgId: args.orgId,
    siteId: args.siteId,
    workflowId: args.context.workflowId,
    scanScopeStart: args.scanScopeStart || args.reportFrom,
    scanScopeEnd: args.scanScopeEnd || args.reportTo,
  });
  const linkedCaseId = String(
    args.context.safetyCaseId || (row ? resolveSafetyCaseId(row) : '') || '',
  ).trim();
  const linkedSafetyCasePath = linkedCaseId
    ? buildLiveSafetyCasePath({
      caseId: linkedCaseId,
      orgId: args.orgId,
      siteId: String(row?.site_id || args.siteId).trim() || undefined,
      workflowId: args.context.workflowId,
      incidentId: args.context.representativeIncidentId || undefined,
    })
    : null;
  const openCurrentScanReview = () => {
    if (currentScanReviewPath) {
      args.navigate(currentScanReviewPath);
      return;
    }
    if (linkedSafetyCasePath) {
      args.navigate(linkedSafetyCasePath);
      return;
    }
    args.onReviewEvidence();
  };
  const hasOffPatternReviewDestination = Boolean(currentScanReviewPath || linkedSafetyCasePath);
  const patternEvidenceReview = args.onContinueReview || args.onReviewEvidence;

  const workflowId = String(row?.workflow_id || args.context.workflowId || '').trim();
  const scheduleSiteId = String(row?.site_id || args.siteId || '').trim();
  const cameraId = String(row?.camera_id || '').trim();
  const zoneId = String(row?.zone_id || '').trim();
  const riskType = String(row?.risk_type || '').trim();
  const scheduleRepeatPath = row
    ? buildRepeatSafetyScanPath({
      sourceWorkflowId: workflowId,
      siteId: scheduleSiteId,
      windowStart: args.scanScopeStart || args.reportFrom || null,
      windowEnd: args.scanScopeEnd || args.reportTo || null,
      cameraId,
      cameraLabel: row.camera_label || null,
      zoneId,
      zoneLabel: row.zone_label || null,
      riskType,
      name: String(
        row.risk_pattern_label
        || row.risk_label
        || row.unsafe_condition_title
        || args.context.riskPatternSummary
        || workflowId,
      ).trim(),
      safetyCaseId: String(
        args.context.safetyCaseId || row.safety_case_id || row.finding_id || '',
      ).trim() || null,
    })
    : null;
  const hasScheduleHandoff = Boolean(scheduleRepeatPath);

  const configureScheduledScans = () => {
    if (!scheduleRepeatPath) return;
    args.navigate(scheduleRepeatPath);
  };

  const createScanDraft = async () => {
    if (!row) return;
    const workflowId = String(row.workflow_id || '').trim();
    if (!workflowId) return;
    const isUpdatedScan = Boolean(args.context.hasNewActivityAfterSignedScan);
    const signedAt = String(
      args.signedScanAt || row.report_finalized_at || row.latest_scan_signed_at || '',
    ).trim() || undefined;
    const selectedScopeStart = String(
      args.scanScopeStart || args.reportFrom || '',
    ).trim() || undefined;
    const selectedScopeEnd = String(
      args.scanScopeEnd || args.reportTo || '',
    ).trim() || undefined;
    const handoff = buildHandoffFromDashboardRow(
      row,
      { orgId: args.orgId, siteId: args.siteId },
      isUpdatedScan
        ? {
            scanScopeStart: selectedScopeStart || signedAt,
            scanScopeEnd: selectedScopeEnd || new Date().toISOString(),
            signedScanAt: signedAt,
            signedScanScopeStart: args.signedScanScopeStart || row.report_scope_start || row.latest_scan_scope_start || undefined,
            signedScanScopeEnd: args.signedScanScopeEnd || row.report_scope_end || row.latest_scan_scope_end || undefined,
            latestSignedScanId: args.latestSignedScanId || row.latest_finalized_report_id || row.report_id || undefined,
            newActivityWindowStart: args.newActivityWindowStart || signedAt,
            isUpdatedScan: true,
          }
        : {
            scanScopeStart: args.reportFrom || undefined,
            scanScopeEnd: args.reportTo || undefined,
            isUpdatedScan: false,
          },
    );
    const openPersistedScan = (reportId: string) => {
      const path = safetyScanVerifyPath(reportId, {
        orgId: args.orgId,
        siteId: String(row.site_id || args.siteId).trim() || null,
        safetyCaseId: resolveSafetyCaseId(row) || args.context.safetyCaseId || null,
        incidentId: args.context.representativeIncidentId || row.representative_incident_id || null,
      });
      if (path) args.navigate(path);
    };
    if (row.active_draft_report_id) {
      openPersistedScan(String(row.active_draft_report_id));
      return;
    }

    const response = await reportsApi.createOrResumeSafetyReportDraft(
      workflowId,
      buildCreateDraftPayloadFromHandoff(handoff),
    );
    const createdReportId = String(response.report?.report_id || '').trim();
    if (!createdReportId) return;
    openPersistedScan(createdReportId);
  };

  const createSafetyCase = async () => {
    if (!row) return;
    const workflowId = String(row.workflow_id || '').trim();
    if (!workflowId) return;
    const saved = await reportsApi.saveSafetyFinding(workflowId, {
      representative_incident_id: row.representative_incident_id || undefined,
      scope: {
        site_id: row.site_id || args.siteId || undefined,
        zones: row.zone_id ? [String(row.zone_id)] : [],
        risk_type: row.risk_type || undefined,
      },
      time_window: {
        start: args.reportFrom || undefined,
        end: args.reportTo || undefined,
      },
      source_pattern_id: workflowId,
      source_group_key: workflowId,
      hazard_category: row.risk_type || undefined,
      finding_type: row.risk_type || undefined,
    });
    const createdCaseId = resolveCreatedSafetyCaseId(saved);
    if (createdCaseId) {
      args.onSafetyCaseCreated?.(createdCaseId);
    }
  };

  // Scheduling belongs after a signed Safety Scan exists (state C), not before the manual loop.
  const scheduleAction = pageState === 'state_c'
    && args.canConfigureScheduledScans === true
    && hasScheduleHandoff
    ? { onConfigureScheduledScans: configureScheduledScans }
    : {};

  if (pageState === 'state_a') {
    return {
      onReviewEvidence: args.onReviewEvidence,
      onCreateSafetyCase: () => createSafetyCase(),
      onMarkFalsePositive: args.onMarkFalsePositive,
      onNeedsMoreReview: args.onNeedsMoreReview,
      currentScanReviewPath: null,
      linkedSafetyCasePath: null,
    };
  }

  if (pageState === 'state_b') {
    const openReportPreparation = () => { void createScanDraft(); };
    return {
      // Quiet metadata link in the blue panel; not a primary CTA.
      onOpenSafetyCase: openSafetyCase,
      onContinueReview: hasOffPatternReviewDestination ? openCurrentScanReview : patternEvidenceReview,
      onReviewEvidence: hasOffPatternReviewDestination ? openCurrentScanReview : patternEvidenceReview,
      // Sole blue-panel CTA when report preparation is ready.
      onOpenReportPreparation: openReportPreparation,
      // Kept for Report preparation / Next Required Action surfaces.
      onCreateSafetyScan: openReportPreparation,
      currentScanReviewPath,
      linkedSafetyCasePath,
    };
  }

  return {
    onOpenSafetyCase: openSafetyCase,
    onViewSignedSafetyScan: () => { void viewSignedScan(); },
    onReviewNewActivity: openCurrentScanReview,
    onReviewEvidence: hasOffPatternReviewDestination ? openCurrentScanReview : args.onReviewEvidence,
    currentScanReviewPath,
    linkedSafetyCasePath,
    ...scheduleAction,
    ...(args.context.hasNewActivityAfterSignedScan
      ? { onCreateUpdatedSafetyScan: () => { void createScanDraft(); } }
      : {}),
  };
}
